import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  LinearProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  FilledButton,
  StyledLoadingButton,
} from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import toast from "../../../helpers/toast";
import PaystackPop from "@paystack/inline-js";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  useGetSubscriptionQuery,
  useGetPlansQuery,
  useStartTrialMutation,
  useSubscribeMutation,
  useVerifySubscriptionMutation,
  useCancelSubscriptionMutation,
  useChangePlanMutation,
  useChangePlanVerifyMutation,
  useTopupCreditsMutation,
  useVerifyCreditsMutation,
  useLazyGetUsageEventsQuery,
  useGetAiAllocationsQuery,
  useSetAiAllocationMutation,
  useDeleteAiAllocationMutation,
  useGetParentRevenueShareQuery,
  useRedeemRevenueShareAsPaygMutation,
  useRedeemRevenueShareAsCashMutation,
  useSwitchBillingModeMutation,
  useRevertBillingModeMutation,
  useUpdateParentPaysConfigMutation,
  useGetReportCreditShareQuery,
  useRedeemReportCreditsAsPaygMutation,
  useRedeemReportCreditsAsCashMutation,
  useTransferQuotaMutation,
} from "../../../app/subscriptionApiSlice";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import { formatUtcDatetime, formatDateOrdinal } from "../../../helpers/dateUtils";

// ─── Helpers ────────────────────────────────────────────────────────────────

// Placeholder config so useFlutterwave hook always has valid input on first render
const EMPTY_FW_CONFIG = {
  public_key: "",
  tx_ref: "placeholder",
  amount: 0,
  currency: "NGN",
  customer: { email: "" },
  customizations: { title: "SchoolApp" },
};

/**
 * Hook that abstracts Paystack (imperative) and Flutterwave (React hook) inline payments.
 * Returns `openInlinePayment(result, onSuccess)` where result is the backend inline config
 * response and onSuccess receives the payment reference string.
 */
function useInlinePayment() {
  const [fwConfig, setFwConfig] = useState(null);
  const pendingRef = useRef(null);
  const handleFlutterPayment = useFlutterwave(fwConfig || EMPTY_FW_CONFIG);

  useEffect(() => {
    if (fwConfig && pendingRef.current) {
      const onSuccess = pendingRef.current;
      handleFlutterPayment({
        callback: (response) => {
          closePaymentModal();
          pendingRef.current = null;
          setFwConfig(null);
          onSuccess(response.tx_ref);
        },
        onClose: () => {
          pendingRef.current = null;
          setFwConfig(null);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwConfig]);

  const openInlinePayment = useCallback((result, onSuccess) => {
    // Deep-clone to avoid Immer-frozen RTK Query response objects
    const config = JSON.parse(JSON.stringify(result.config));
    const gatewayKey = result.gateway === "paystack" ? config.key : config.public_key;
    if (!gatewayKey) {
      const other = result.gateway === "paystack" ? "Flutterwave" : "Paystack";
      toast.error(`${result.gateway === "paystack" ? "Paystack" : "Flutterwave"} is not available. Please select ${other} instead.`);
      return;
    }
    if (result.gateway === "paystack") {
      const popup = new PaystackPop();
      popup.newTransaction({
        ...config,
        onSuccess: (tx) => onSuccess(tx.reference),
        onCancel: () => toast.info("Payment cancelled"),
      });
    } else {
      pendingRef.current = onSuccess;
      setFwConfig(config);
    }
  }, []);

  return openInlinePayment;
}

const PLAN_ORDER = ["free", "basic", "premium", "pro"];

const PLAN_COLORS = {
  free: "#9e9e9e",
  basic: "#1976d2",
  premium: "#7b1fa2",
  pro: "#d32f2f",
};

const statusColor = (status) => {
  const map = {
    active: "success",
    trial: "info",
    cancelled: "warning",
    expired: "error",
    "trial expired": "error",
    inactive: "default",
  };
  return map[status?.toLowerCase()] || "default";
};

const pct = (used, limit) => (limit > 0 ? Math.min((used / limit) * 100, 100) : 0);

const barColor = (used, limit) => {
  const p = pct(used, limit);
  if (p >= 90) return "error";
  if (p >= 70) return "warning";
  return "primary";
};

const formatFeatureLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bAi\b/g, "AI");

const PLAN_QUOTA_LABELS = {
  reports: "Reports Generated",
  quizzes: "Quizzes Created",
  invoices: "Invoices Created",
  ai_credits: "AI Credits",
};

const formatQuotaValue = (limit) => {
  if (limit === null || limit === undefined) return "Not specified";
  if (limit === -1) return "Unlimited";
  if (limit === 0) return "PAYG only";
  return `${Number(limit).toLocaleString()} per term`;
};

const getPlanQuotaEntries = (plan) => {
  const quota = plan?.quota || {};
  const aiQuota = plan?.ai_quota || {};
  const entries = [
    { key: "reports", label: PLAN_QUOTA_LABELS.reports, value: quota.reports },
    { key: "quizzes", label: PLAN_QUOTA_LABELS.quizzes, value: quota.quizzes },
    { key: "invoices", label: PLAN_QUOTA_LABELS.invoices, value: quota.invoices },
    {
      key: "ai_credits",
      label: PLAN_QUOTA_LABELS.ai_credits,
      value: aiQuota.credits,
    },
  ];
  return entries.filter((entry) => entry.value !== null && entry.value !== undefined);
};

function openCheckoutPopup(url) {
  const width = 520;
  const height = 760;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  const features = `width=${Math.round(width)},height=${Math.round(height)},left=${Math.round(left)},top=${Math.round(top)},popup=yes,resizable=yes,scrollbars=yes`;
  const popup = window.open(url, "paystack_checkout", features);
  if (popup) popup.focus();
  return popup;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const TRANSFERABLE_QUOTAS = [
  { key: "reports", label: "Reports" },
  { key: "quizzes", label: "Quizzes" },
  { key: "invoices", label: "Invoices" },
];

function QuotaTransferDialog({ open, onClose, quota, usage, onSuccess }) {
  const [fromQuota, setFromQuota] = useState("reports");
  const [toQuota, setToQuota] = useState("quizzes");
  const [amount, setAmount] = useState(50);
  const [transferQuota, { isLoading }] = useTransferQuotaMutation();

  // Compute effective limits (plan limit + any prior adjustments)
  const getEffectiveLimit = (key) => {
    const planLimit = quota[key];
    if (planLimit === null || planLimit === undefined || planLimit === -1 || planLimit === 0) return planLimit;
    const adj = usage?.[`${key}_quota_adjustment`] ?? 0;
    return planLimit + adj;
  };

  const getUsed = (key) => usage?.[`${key}_used`] ?? 0;
  const getRemaining = (key) => {
    const lim = getEffectiveLimit(key);
    if (!lim || lim === -1) return null;
    return Math.max(0, lim - getUsed(key));
  };

  const fromRemaining = getRemaining(fromQuota);
  const fromEffective = getEffectiveLimit(fromQuota);
  const toEffective = getEffectiveLimit(toQuota);

  const isTransferable = (key) => {
    const lim = quota[key];
    return lim !== null && lim !== undefined && lim !== -1 && lim !== 0;
  };

  const transferableQuotas = TRANSFERABLE_QUOTAS.filter((q) => isTransferable(q.key));

  const handleSubmit = async () => {
    if (fromQuota === toQuota) {
      toast.error("Source and destination must be different.");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Please enter a positive amount.");
      return;
    }
    try {
      const res = await transferQuota({ from_quota: fromQuota, to_quota: toQuota, amount }).unwrap();
      toast.success(res.message);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Transfer failed.");
    }
  };

  // Prevent selecting same quota for both sides
  const toOptions = TRANSFERABLE_QUOTAS.filter((q) => q.key !== fromQuota && isTransferable(q.key));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Redistribute Quota</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Move unused quota from one category to another within the current period. Only unused
          quota can be transferred.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Transfer From</InputLabel>
          <Select
            value={fromQuota}
            label="Transfer From"
            onChange={(e) => {
              setFromQuota(e.target.value);
              if (e.target.value === toQuota) {
                const other = transferableQuotas.find((q) => q.key !== e.target.value);
                if (other) setToQuota(other.key);
              }
            }}
          >
            {transferableQuotas.map((q) => (
              <MenuItem key={q.key} value={q.key}>
                {q.label} (remaining: {getRemaining(q.key) ?? "N/A"})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Transfer To</InputLabel>
          <Select
            value={toOptions.some((q) => q.key === toQuota) ? toQuota : (toOptions[0]?.key ?? "")}
            label="Transfer To"
            onChange={(e) => setToQuota(e.target.value)}
          >
            {toOptions.map((q) => (
              <MenuItem key={q.key} value={q.key}>
                {q.label} (current limit: {getEffectiveLimit(q.key) ?? "N/A"})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Amount to Transfer"
          type="number"
          fullWidth
          size="small"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          inputProps={{ min: 1, step: 1, max: fromRemaining ?? undefined }}
          helperText={
            fromRemaining !== null
              ? `Max transferable: ${fromRemaining}`
              : "Source has no finite quota"
          }
        />
        {fromRemaining !== null && toEffective !== null && amount > 0 && fromQuota !== toQuota && (
          <Alert severity="info" sx={{ mt: 2 }}>
            After transfer:{" "}
            <strong>{TRANSFERABLE_QUOTAS.find((q) => q.key === fromQuota)?.label}</strong>{" "}
            limit → {(fromEffective ?? 0) - amount} &nbsp;|&nbsp;{" "}
            <strong>{TRANSFERABLE_QUOTAS.find((q) => q.key === toQuota)?.label}</strong>{" "}
            limit → {(toEffective ?? 0) + amount}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <StyledLoadingButton
          loading={isLoading}
          onClick={handleSubmit}
          disabled={!amount || amount <= 0 || fromQuota === toQuota || transferableQuotas.length < 2}
        >
          Transfer
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function QuotaBar({ label, used, limit }) {
  if (limit === null || limit === undefined) return null;
  const unlimited = limit === -1;
  const blocked = limit === 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" color={blocked ? "text.disabled" : "text.secondary"}>
          {unlimited ? `${used} / unlimited` : blocked ? "PAYG only" : `${used} / ${limit}`}
        </Typography>
      </Box>
      {!unlimited && !blocked && (
        <LinearProgress
          variant="determinate"
          value={pct(used, limit)}
          color={barColor(used, limit)}
          sx={{ borderRadius: 2, height: 8 }}
        />
      )}
    </Box>
  );
}

// ─── Gateway + cycle selector inside dialogs ────────────────────────────────

function GatewaySelector({ gateway, onChange }) {
  return (
    <Box sx={{ mt: 2 }}>
      <FormLabel component="legend">
        <Typography variant="formSubHeading">PAYMENT GATEWAY</Typography>
      </FormLabel>
      <RadioGroup row value={gateway} onChange={(e) => onChange(e.target.value)}>
        <FormControlLabel value="paystack" control={<Radio size="small" />} label="Paystack" />
        <FormControlLabel
          value="flutterwave"
          control={<Radio size="small" />}
          label="Flutterwave"
        />
      </RadioGroup>
    </Box>
  );
}

// (BillingCycleSelector removed — all subscriptions are now term-based, 4 months)

// ─── Topup Credits Modal ─────────────────────────────────────────────────────

function TopupCreditsModal({ open, onClose, onSuccess }) {
  const [credits, setCredits] = useState(50);
  const [gateway, setGateway] = useState("paystack");
  const [couponCode, setCouponCode] = useState("");
  const [pricing, setPricing] = useState(null);
  const [pendingRef, setPendingRef] = useState(null);
  const [topupCredits, { isLoading }] = useTopupCreditsMutation();
  const [verifyCredits, { isLoading: verifying }] = useVerifyCreditsMutation();
  const openInlinePayment = useInlinePayment();

  useEffect(() => {
    setPricing(null);
  }, [credits, couponCode, gateway]);

  const handlePay = async () => {
    if (credits < 50) {
      toast.error("Minimum top-up is 50 credits (₦500).");
      return;
    }
    try {
      const result = await topupCredits({
        credits,
        gateway,
        ...(couponCode.trim() ? { coupon_code: couponCode.trim() } : {}),
      }).unwrap();
      setPricing({
        original: result.original_amount_naira ?? credits * 10,
        discount: result.discount_amount_naira ?? 0,
        final: result.amount_naira,
        coupon: result.coupon,
      });

      if (result.requires_payment === false) {
        const r = await verifyCredits({ reference: result.reference, gateway }).unwrap();
        toast.success(r.message || "Credits added!");
        setPendingRef(null);
        onSuccess?.();
        return;
      }

      try {
        openInlinePayment(result, async (reference) => {
          try {
            const r = await verifyCredits({ reference, gateway }).unwrap();
            toast.success(r.message || "Credits added!");
            setPendingRef(null);
            onSuccess?.();
          } catch (err) {
            toast.error(err?.message || "Payment verification failed.");
          }
        });
      } catch (inlineErr) {
        if (result.gateway === "paystack" && result.payment_url) {
          setPendingRef(result.reference);
          openCheckoutPopup(result.payment_url);
          toast.info("Inline checkout could not start. Complete payment in the popup, then click Verify.");
        } else {
          throw inlineErr;
        }
      }
    } catch (err) {
      toast.error(err?.message || "Could not initialize payment.");
    }
  };

  const handleVerifyPaystack = async () => {
    if (!pendingRef) return;
    try {
      const r = await verifyCredits({ reference: pendingRef, gateway: "paystack" }).unwrap();
      toast.success(r.message || "Credits added!");
      setPendingRef(null);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.message || "Payment verification failed.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Top-Up PAYG Credits</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          ₦10 = 1 credit. Use credits when your plan quota is exhausted. Minimum 50 credits.
        </Typography>
        <TextField
          label="Credits to add"
          type="number"
          fullWidth
          value={credits}
          onChange={(e) => setCredits(Number(e.target.value))}
          inputProps={{ min: 50, step: 50 }}
          helperText={`= ₦${(credits * 10).toLocaleString()}`}
          size="small"
        />
        <TextField
          label="Coupon Code (optional)"
          fullWidth
          size="small"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          sx={{ mt: 2 }}
        />
        {pricing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Original: ₦{Number(pricing.original || 0).toLocaleString()} | Discount: ₦{Number(pricing.discount || 0).toLocaleString()} | Payable: ₦{Number(pricing.final || 0).toLocaleString()}
          </Alert>
        )}
        <GatewaySelector gateway={gateway} onChange={setGateway} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FilledButton onClick={onClose} sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}>
          Cancel
        </FilledButton>
        <StyledLoadingButton loading={isLoading || verifying} onClick={handlePay}>
          Proceed to Payment
        </StyledLoadingButton>
        {pendingRef && (
          <FilledButton onClick={handleVerifyPaystack}>
            I Have Paid - Verify
          </FilledButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Plan Card ───────────────────────────────────────────────────────────────

function PlanCard({ plan, currentPlanKey, subscriptionIsActive, onSelect, onViewDetails }) {
  const isCurrent = plan.key === currentPlanKey;
  // Only treat as a downgrade if the subscription is actually active — an expired
  // or cancelled subscription has no "current" plan to downgrade from.
  const isDowngrade =
    subscriptionIsActive &&
    PLAN_ORDER.indexOf(plan.key) < PLAN_ORDER.indexOf(currentPlanKey);

  return (
    <Box
      sx={(theme) => ({
        border: `2px solid ${isCurrent ? PLAN_COLORS[plan.key] : theme.palette.mode === "dark" ? "#444" : "#e0e0e0"}`,
        borderRadius: 2,
        p: 2.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background: isCurrent
          ? theme.palette.mode === "dark" ? "#1a1a2e" : "#fafafe"
          : theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
      })}
    >
      {isCurrent && (
        <Chip
          label="Current Plan"
          size="small"
          color="primary"
          sx={{ position: "absolute", top: 12, right: 12, fontSize: 10 }}
        />
      )}
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: PLAN_COLORS[plan.key], mb: 0.5 }}
      >
        {plan.display_name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {plan.description}
      </Typography>

      {plan.price?.term != null && (
        <Box sx={{ mb: 1.5 }}>
          {plan.price.term === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Free forever
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                <strong>₦{plan.price.term?.toLocaleString()}</strong>
                <Typography component="span" variant="caption" color="text.secondary"> / term (4 months)</Typography>
              </Typography>
              {plan.price.year != null && (
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  <strong>₦{plan.price.year?.toLocaleString()}</strong>
                  <Typography component="span" variant="caption" color="success.main"> / year (save 10%)</Typography>
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2, flexGrow: 1 }}>
        {plan.features &&
          Object.entries(plan.features)
            .filter(([, enabled]) => enabled)
            .map(([feat]) => (
              <Box key={feat} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.main" }} />
                <Typography variant="body2">
                  {formatFeatureLabel(feat)}
                </Typography>
              </Box>
            ))}
      </Box>

      <FilledButton
        fullWidth
        startIcon={<InfoOutlinedIcon />}
        sx={(theme) => ({
          mb: 1,
          background: theme.palette.mode === "dark" ? "#2a2a2a" : "#f3f5f7",
          color: theme.palette.mode === "dark" ? "#f1f1f1" : "#1f2a37",
          "&:hover": {
            background: theme.palette.mode === "dark" ? "#353535" : "#e8edf2",
            color: theme.palette.mode === "dark" ? "#f1f1f1" : "#111827",
          },
        })}
        onClick={() => onViewDetails(plan)}
      >
        See Details
      </FilledButton>

      {!isCurrent && plan.key !== "free" && (
        <FilledButton
          fullWidth
          sx={
            isDowngrade
              ? { background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }
              : {}
          }
          onClick={() => onSelect(plan)}
        >
          {isDowngrade ? "Downgrade" : "Upgrade"} to {plan.display_name}
        </FilledButton>
      )}
      {isCurrent && !subscriptionIsActive && plan.key !== "free" && (
        <FilledButton
          fullWidth
          startIcon={<UpgradeIcon />}
          onClick={() => onSelect(plan)}
        >
          Renew {plan.display_name}
        </FilledButton>
      )}
      {!isCurrent && plan.key === "free" && (
        <FilledButton
          fullWidth
          sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
          onClick={() => onSelect(plan)}
        >
          Downgrade to Free
        </FilledButton>
      )}
    </Box>
  );
}

function PlanDetailsModal({ open, onClose, plan }) {
  if (!plan) return null;

  const featureEntries = Object.entries(plan.features || {});
  const activeFeatures = featureEntries.filter(([, value]) => Boolean(value));
  const inactiveFeatures = featureEntries.filter(([, value]) => !Boolean(value));
  const quotaEntries = getPlanQuotaEntries(plan);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{plan.display_name} Plan Details</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {plan.description || "No description available for this plan."}
        </Typography>

        <Typography variant="formSubHeading">Pricing</Typography>
        <List dense sx={{ pt: 0.5, mb: 1.5 }}>
          <ListItem disableGutters>
            <ListItemText
              primary={
                plan.price?.term === 0
                  ? "Free forever"
                  : `Term (4 months): ${plan.price?.term != null ? `₦${Number(plan.price.term).toLocaleString()}` : "Not specified"}`
              }
            />
          </ListItem>
          {plan.price?.year != null && (
            <ListItem disableGutters>
              <ListItemText primary={`Annual: ₦${Number(plan.price.year).toLocaleString()} (save 10%)`} />
            </ListItem>
          )}
        </List>

        <Typography variant="formSubHeading">What's Included</Typography>
        {activeFeatures.length > 0 ? (
          <List dense sx={{ pt: 0.5, mb: 1.5 }}>
            {activeFeatures.map(([key]) => (
              <ListItem key={key} disableGutters>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
                </ListItemIcon>
                <ListItemText primary={formatFeatureLabel(key)} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            No feature list provided for this plan.
          </Typography>
        )}

        {quotaEntries.length > 0 && (
          <>
            <Typography variant="formSubHeading">Feature Quotas</Typography>
            <List dense sx={{ pt: 0.5, mb: 1.5 }}>
              {quotaEntries.map((entry) => (
                <ListItem key={entry.key} disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.label}
                    secondary={formatQuotaValue(entry.value)}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {inactiveFeatures.length > 0 && (
          <>
            <Typography variant="formSubHeading">Not Included</Typography>
            <List dense sx={{ pt: 0.5 }}>
              {inactiveFeatures.map(([key]) => (
                <ListItem key={key} disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CancelOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                  </ListItemIcon>
                  <ListItemText primary={formatFeatureLabel(key)} />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FilledButton
          onClick={onClose}
          sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
        >
          Close
        </FilledButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Plan Selection / Payment Modal ─────────────────────────────────────────

function PlanPaymentModal({ open, onClose, selectedPlan, currentPlanKey, subscriptionIsActive, onSuccess }) {
  const [billing, setBilling] = useState("term");
  const [gateway, setGateway] = useState("paystack");
  const [couponCode, setCouponCode] = useState("");
  const [pricing, setPricing] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [subscribe, { isLoading: subscribing }] = useSubscribeMutation();
  const [verifySubscription, { isLoading: verifying }] = useVerifySubscriptionMutation();
  const [changePlan, { isLoading: changing }] = useChangePlanMutation();
  const [changePlanVerify, { isLoading: verifyingChange }] = useChangePlanVerifyMutation();
  const openInlinePayment = useInlinePayment();

  useEffect(() => {
    if (open) {
      setCouponCode("");
      setPricing(null);
      setPendingPayment(null);
    }
  }, [open, selectedPlan?.key]);

  if (!selectedPlan) return null;

  // Only treat as a downgrade if the subscription is actually active — an expired
  // or cancelled subscription has no "current" plan to downgrade from.
  const isDowngrade =
    subscriptionIsActive &&
    PLAN_ORDER.indexOf(selectedPlan.key) < PLAN_ORDER.indexOf(currentPlanKey);
  const isFreeDowngrade = selectedPlan.key === "free";

  const handleConfirm = async () => {
    try {
      if (isFreeDowngrade) {
        // Downgrade to free — no payment needed regardless of subscription status
        const result = await changePlan({ plan: "free" }).unwrap();
        toast.success(result.message);
        onSuccess?.();
        onClose();
        return;
      } else if (!subscriptionIsActive || currentPlanKey === "free" || !currentPlanKey) {
        // New subscription
        const result = await subscribe({
          plan: selectedPlan.key,
          gateway,
          billing_cycle: billing,
          ...(couponCode.trim() ? { coupon_code: couponCode.trim() } : {}),
        }).unwrap();
        setPricing({
          original: result.original_amount_naira ?? selectedPlan.price?.[billing],
          discount: result.discount_amount_naira ?? 0,
          final: result.amount_naira,
          coupon: result.coupon,
        });

        if (result.requires_payment === false) {
          const r = await verifySubscription({
            reference: result.reference,
            gateway,
            plan: selectedPlan.key,
            billing_cycle: billing,
          }).unwrap();
          toast.success(r.message || "Subscription activated!");
          setPendingPayment(null);
          onSuccess?.();
          onClose();
          return;
        }

        try {
          openInlinePayment(result, async (reference) => {
            try {
              const r = await verifySubscription({
                reference,
                gateway,
                plan: selectedPlan.key,
                billing_cycle: billing,
              }).unwrap();
              toast.success(r.message || "Subscription activated!");
              setPendingPayment(null);
              onSuccess?.();
              onClose();
            } catch (err) {
              toast.error(err?.message || "Payment verification failed.");
            }
          });
        } catch (inlineErr) {
          if (result.gateway === "paystack" && result.payment_url) {
            setPendingPayment({
              reference: result.reference,
              flow: "subscribe",
              plan: selectedPlan.key,
              billing,
            });
            openCheckoutPopup(result.payment_url);
            toast.info("Inline checkout could not start. Complete payment in the popup, then click Verify.");
            return;
          }
          throw inlineErr;
        }
      } else if (isDowngrade) {
        // Downgrade — no payment needed
        const result = await changePlan({ plan: selectedPlan.key }).unwrap();
        toast.success(result.message);
        onSuccess?.();
        onClose();
      } else {
        // Upgrade — inline payment
        const result = await changePlan({
          plan: selectedPlan.key,
          gateway,
          billing_cycle: billing,
          ...(couponCode.trim() ? { coupon_code: couponCode.trim() } : {}),
        }).unwrap();
        setPricing({
          original: result.original_amount_naira ?? selectedPlan.price?.[billing],
          discount: result.discount_amount_naira ?? 0,
          final: result.amount_naira,
          coupon: result.coupon,
        });
        if (result.action === "upgrade") {
          if (result.requires_payment === false) {
            const r = await changePlanVerify({
              reference: result.reference,
              gateway,
              plan: selectedPlan.key,
              billing_cycle: billing,
            }).unwrap();
            toast.success(r.message || "Plan upgraded!");
            setPendingPayment(null);
            onSuccess?.();
            onClose();
            return;
          }

          try {
            openInlinePayment(result, async (reference) => {
              try {
                const r = await changePlanVerify({
                  reference,
                  gateway,
                  plan: selectedPlan.key,
                  billing_cycle: billing,
                }).unwrap();
                toast.success(r.message || "Plan upgraded!");
                setPendingPayment(null);
                onSuccess?.();
                onClose();
              } catch (err) {
                toast.error(err?.message || "Payment verification failed.");
              }
            });
          } catch (inlineErr) {
            if (result.gateway === "paystack" && result.payment_url) {
              setPendingPayment({
                reference: result.reference,
                flow: "upgrade",
                plan: selectedPlan.key,
                billing,
              });
              openCheckoutPopup(result.payment_url);
              toast.info("Inline checkout could not start. Complete payment in the popup, then click Verify.");
              return;
            }
            throw inlineErr;
          }
        }
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    }
  };

  const handleVerifyPaystack = async () => {
    if (!pendingPayment) return;
    try {
      if (pendingPayment.flow === "subscribe") {
        const r = await verifySubscription({
          reference: pendingPayment.reference,
          gateway: "paystack",
          plan: pendingPayment.plan,
          billing_cycle: pendingPayment.billing,
        }).unwrap();
        toast.success(r.message || "Subscription activated!");
      } else {
        const r = await changePlanVerify({
          reference: pendingPayment.reference,
          gateway: "paystack",
          plan: pendingPayment.plan,
          billing_cycle: pendingPayment.billing,
        }).unwrap();
        toast.success(r.message || "Plan upgraded!");
      }
      setPendingPayment(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Payment verification failed.");
    }
  };

  const isWorking = subscribing || verifying || changing || verifyingChange;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {selectedPlan.key === currentPlanKey
          ? `Renew ${selectedPlan.display_name}`
          : isDowngrade
          ? `Downgrade to ${selectedPlan.display_name}`
          : `Upgrade to ${selectedPlan.display_name}`}
      </DialogTitle>
      <DialogContent>
        {isFreeDowngrade ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Downgrading to Free will remove access to paid features at the end of your current
            billing period.
          </Alert>
        ) : isDowngrade ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Downgrade will take effect at your next renewal date.
          </Alert>
        ) : null}

        {!isFreeDowngrade && !isDowngrade && (
          <>
            <Box sx={{ mt: 2 }}>
              <FormLabel component="legend">
                <Typography variant="formSubHeading">BILLING CYCLE</Typography>
              </FormLabel>
              <RadioGroup row value={billing} onChange={(e) => setBilling(e.target.value)}>
                <FormControlLabel
                  value="term"
                  control={<Radio size="small" />}
                  label={`Term — ₦${selectedPlan.price?.term?.toLocaleString() ?? "—"}`}
                />
                {selectedPlan.price?.year != null && (
                  <FormControlLabel
                    value="year"
                    control={<Radio size="small" />}
                    label={`Annual — ₦${selectedPlan.price?.year?.toLocaleString()} (save 10%)`}
                  />
                )}
              </RadioGroup>
            </Box>
            <GatewaySelector gateway={gateway} onChange={setGateway} />
            <TextField
              label="Coupon Code (optional)"
              fullWidth
              size="small"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              sx={{ mt: 2 }}
            />
            {pricing && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Original: ₦{Number(pricing.original || 0).toLocaleString()} | Discount: ₦{Number(pricing.discount || 0).toLocaleString()} | Payable: ₦{Number(pricing.final || 0).toLocaleString()}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FilledButton
          onClick={onClose}
          sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
        >
          Cancel
        </FilledButton>
        <StyledLoadingButton loading={isWorking} onClick={handleConfirm}>
          {isFreeDowngrade
            ? "Confirm Downgrade"
            : isDowngrade
            ? "Schedule Downgrade"
            : selectedPlan.key === currentPlanKey
            ? "Proceed to Renew"
            : "Proceed to Payment"}
        </StyledLoadingButton>
        {pendingPayment && (
          <FilledButton onClick={handleVerifyPaystack}>
            I Have Paid - Verify
          </FilledButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Trial Modal ─────────────────────────────────────────────────────────────

function TrialModal({ open, onClose, onSuccess }) {
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [startTrial, { isLoading }] = useStartTrialMutation();

  const handleStart = async () => {
    try {
      const result = await startTrial({ plan: selectedPlan }).unwrap();
      toast.success(result.message);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Could not start trial.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Start Your 30-Day Free Trial</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Try a paid plan free for 30 days. No credit card required.
        </Typography>
        <FormLabel>
          <Typography variant="formSubHeading">SELECT TRIAL PLAN</Typography>
        </FormLabel>
        <RadioGroup value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
          <FormControlLabel value="basic" control={<Radio size="small" />} label="Basic" />
          <FormControlLabel value="premium" control={<Radio size="small" />} label="Premium" />
          <FormControlLabel value="pro" control={<Radio size="small" />} label="Pro" />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FilledButton
          onClick={onClose}
          sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
        >
          Cancel
        </FilledButton>
        <StyledLoadingButton loading={isLoading} onClick={handleStart}>
          Start Trial
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Activity Log ────────────────────────────────────────────────────────────

const ACTION_TYPE_LABELS = {
  reports:     "Report Generated",
  quizzes:     "Quiz Created",
  invoices:    "Invoice Created",
  ai_comment:  "AI Comment",
  ai_question: "AI Question",
  ai_quiz:     "AI Quiz",
};

const SOURCE_LABELS = {
  subscription_quota: "Plan Quota",
  school_payg:        "School PAYG",
  teacher_wallet:     "Teacher Credits",
  student_free:       "Student Free Quota",
  student_wallet:     "Student Wallet",
};

function ActivityLog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [actionFilter, setActionFilter] = useState("");

  const [fetchEvents, { data, isFetching }] = useLazyGetUsageEventsQuery();

  // Fetch on mount and whenever page/filter changes
  useEffect(() => {
    fetchEvents({ page: page + 1, per_page: perPage, ...(actionFilter ? { action_type: actionFilter } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, actionFilter]);

  const events = data?.events || [];
  const total  = data?.total  || 0;

  return (
    <StyledPaper sx={{ padding: "0 0 24px 0" }}>
      <Typography variant="formHeading" sx={{ padding: "20px" }}>
        Usage Activity Log
      </Typography>
      <Divider sx={{ margin: "0 20px 20px 20px" }} />
      <Box sx={{ px: 2.5 }}>
        {/* Filter row */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="action-filter-label">Filter by action</InputLabel>
            <Select
              labelId="action-filter-label"
              label="Filter by action"
              value={actionFilter}
              onChange={(e) => { setPage(0); setActionFilter(e.target.value); }}
            >
              <MenuItem value="">All actions</MenuItem>
              {Object.entries(ACTION_TYPE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {isFetching && <CircularProgress size={20} />}
        </Box>

        {isMobile ? (
          <Box>
            {!isFetching && events.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No activity recorded yet.</Typography>
              </Paper>
            )}
            {events.map((ev) => (
              <Paper key={ev.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatUtcDatetime(ev.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {ACTION_TYPE_LABELS[ev.action_type] || ev.action_type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ev.actor_name || "system"}
                </Typography>
                {ev.details && (
                  <Typography variant="caption" color="text.secondary">
                    {ev.details}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={SOURCE_LABELS[ev.source] || ev.source || "—"}
                    color={
                      ev.source === "subscription_quota" ? "primary" :
                      ev.source === "school_payg" ? "warning" : "default"
                    }
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    {ev.source === "subscription_quota"
                      ? `×${ev.amount}`
                      : `${ev.amount?.toLocaleString()} credits`}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Who</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isFetching && events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No activity recorded yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {events.map((ev) => (
                  <TableRow key={ev.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatUtcDatetime(ev.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ev.actor_name || <em style={{ color: "#9e9e9e" }}>system</em>}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ACTION_TYPE_LABELS[ev.action_type] || ev.action_type}
                      </Typography>
                      {ev.details && (
                        <Typography variant="caption" color="text.secondary">
                          {ev.details}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={SOURCE_LABELS[ev.source] || ev.source || "—"}
                        color={
                          ev.source === "subscription_quota" ? "primary" :
                          ev.source === "school_payg" ? "warning" : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {ev.source === "subscription_quota"
                          ? `×${ev.amount}`
                          : `${ev.amount?.toLocaleString()} credits`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={perPage}
          rowsPerPageOptions={[perPage]}
          sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
        />
      </Box>
    </StyledPaper>
  );
}

// ─── AI Allocations ──────────────────────────────────────────────────────────

function AllocationDialog({ open, onClose, existing, onSave }) {
  const [teacherSearch, setTeacherSearch] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [credits, setCredits] = useState(100);
  const [setAllocation, { isLoading }] = useSetAiAllocationMutation();

  // Fetch up to 50 teachers matching the search term
  const { data: teachersData } = useGetTeachersPaginatedQuery(
    { page: 1, per_page: 50, q: teacherSearch },
    { skip: !open }
  );
  const teachers = teachersData?.teachers || [];

  // Pre-populate when editing an existing allocation
  useEffect(() => {
    if (existing) {
      setTeacherId(String(existing.teacher_id));
      setCredits(existing.credits_allocated);
    } else {
      setTeacherId("");
      setCredits(100);
    }
    setTeacherSearch("");
  }, [existing, open]);

  const handleSave = async () => {
    if (!teacherId || credits < 1) {
      toast.error("Select a teacher and enter credits ≥ 1.");
      return;
    }
    try {
      await setAllocation({ teacher_id: Number(teacherId), credits_allocated: credits }).unwrap();
      toast.success("Allocation saved.");
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || "Could not save allocation.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{existing ? "Edit Allocation" : "Allocate AI Credits"}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set the number of school AI credits this teacher may use per billing period. Credits
          draw from the school's Pro quota first, then from PAYG.
        </Typography>

        {!existing && (
          <Autocomplete
            options={teachers}
            getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ""}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            value={teachers.find((t) => String(t.id) === teacherId) || null}
            onChange={(_, value) => setTeacherId(String(value?.id || ""))}
            onInputChange={(_, value, reason) => { if (reason === "input") setTeacherSearch(value); }}
            filterOptions={(x) => x}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search teachers"
                size="small"
                placeholder="Type a name to filter"
              />
            )}
          />
        )}

        {existing && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Teacher:</strong> {existing.teacher_name}
          </Typography>
        )}

        <TextField
          label="Credits allocated per period"
          type="number"
          fullWidth
          size="small"
          value={credits}
          onChange={(e) => setCredits(Number(e.target.value))}
          inputProps={{ min: 1, step: 10 }}
          helperText="Combined cap across all AI actions within one billing period"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <FilledButton
          onClick={onClose}
          sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
        >
          Cancel
        </FilledButton>
        <StyledLoadingButton loading={isLoading} onClick={handleSave}>
          Save
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function ParentRevenueShare() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const { data, isFetching, refetch } = useGetParentRevenueShareQuery({ page: page + 1, per_page: rowsPerPage });
  const [redeemAsPayg, { isLoading: redeemingPayg }] = useRedeemRevenueShareAsPaygMutation();
  const [redeemAsCash, { isLoading: redeemingCash }] = useRedeemRevenueShareAsCashMutation();

  const rows = data?.shares || [];
  const pendingNaira = data?.pending_total_naira ?? 0;
  const totalCount = data?.total ?? 0;

  const handleRedeemPayg = async () => {
    try {
      const result = await redeemAsPayg().unwrap();
      toast.success(result.message || "Redeemed as PAYG credits!");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Could not redeem credits.");
    }
  };

  const handleRedeemCash = async () => {
    try {
      const result = await redeemAsCash().unwrap();
      toast.success(result.message || "Cash payout request sent to support.");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Could not request cash payout.");
    }
  };

  const fmtDate = (iso) => iso ? formatDateOrdinal(iso) : "—";

  return (
    <StyledPaper sx={{ padding: "0 0 24px 0" }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Typography variant="formHeading">Parent Purchase Revenue Share</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          5% of each parent's learning-game credit purchase is attributed to your school.
        </Typography>
      </Box>
      {isFetching && <LinearProgress />}
      <Divider sx={{ mx: 2.5, mb: 2.5 }} />

      {/* Pending balance card */}
      <Box sx={{ px: 2.5, mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, justifyContent: "space-between" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Pending Balance</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>₦{pendingNaira.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <StyledLoadingButton
              loading={redeemingPayg}
              disabled={pendingNaira === 0}
              onClick={handleRedeemPayg}
            >
              Redeem as PAYG Credits
            </StyledLoadingButton>
            <StyledLoadingButton
              loading={redeemingCash}
              disabled={pendingNaira === 0}
              sx={{ background: "#388e3c", "&:hover": { color: "#388e3c" } }}
              onClick={handleRedeemCash}
            >
              Request Cash Payout
            </StyledLoadingButton>
          </Box>
        </Paper>
      </Box>

      {/* History table */}
      <Box sx={{ px: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Share History</Typography>
        {isMobile ? (
          <Box>
            {rows.length === 0 && !isFetching && (
              <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No share records yet.</Typography>
              </Paper>
            )}
            {rows.map((row) => (
              <Paper key={row.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₦{row.amount_naira?.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">{fmtDate(row.created_at)}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                  <Chip
                    label={row.status?.replace(/_/g, " ")}
                    size="small"
                    color={row.status === "pending" ? "warning" : "success"}
                  />
                  <Typography variant="caption" color="text.secondary">Ref: {row.tx_reference || "—"}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Redeemed: {fmtDate(row.redeemed_at)}
                </Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{"& th": { fontWeight: 600 } }}>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount (₦)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Redeemed At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !isFetching && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No share records yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{fmtDate(row.created_at)}</TableCell>
                    <TableCell align="right">₦{row.amount_naira?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status?.replace(/_/g, " ")}
                        size="small"
                        color={row.status === "pending" ? "warning" : "success"}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{row.tx_reference || "—"}</TableCell>
                    <TableCell>{fmtDate(row.redeemed_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
          onPageChange={(_, newPage) => setPage(newPage)}
          sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
        />
      </Box>
    </StyledPaper>
  );
}

function AIAllocations() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isFetching, refetch } = useGetAiAllocationsQuery();
  const [deleteAllocation, { isLoading: deleting }] = useDeleteAiAllocationMutation();

  const allocations = data?.allocations || [];
  const allocationModeActive = allocations.length > 0;

  const handleEdit = (alloc) => {
    setEditing(alloc);
    setDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAllocation(deleteTarget.teacher_id).unwrap();
      toast.success("Allocation removed.");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err?.data?.message || "Could not remove allocation.");
    }
  };

  return (
    <StyledPaper sx={{ padding: "0 0 24px 0" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 2.5,
          py: 2.5,
        }}
      >
        <Typography variant="formHeading">Teacher AI Allocations</Typography>
        <FilledButton startIcon={<PersonOutlineIcon />} onClick={handleAddNew}>
          Allocate Teacher
        </FilledButton>
      </Box>
      {isFetching && <LinearProgress />}
      <Divider sx={{ mx: 2.5, mb: 2 }} />

      <Box sx={{ px: 2.5 }}>
        {allocationModeActive ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Allocation mode is active.</strong> Only teachers listed below may draw from
            the school AI pool. Teachers not listed are limited to their personal AI wallet.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>No allocations set.</strong> All teachers can freely draw from the school AI
            pool. Add at least one allocation to enable per-teacher caps.
          </Alert>
        )}

        {isMobile ? (
          <Box>
            {!isFetching && allocations.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No allocations yet.</Typography>
              </Paper>
            )}
            {allocations.map((alloc) => {
              const usedPct =
                alloc.credits_allocated > 0
                  ? Math.min((alloc.credits_used / alloc.credits_allocated) * 100, 100)
                  : 0;
              const barCol = usedPct >= 90 ? "error" : usedPct >= 70 ? "warning" : "primary";
              return (
                <Paper key={alloc.teacher_id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {alloc.teacher_name || `Teacher #${alloc.teacher_id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Allocated: {alloc.credits_allocated}</Typography>
                  <Typography variant="body2" color="text.secondary">Used: {alloc.credits_used}</Typography>
                  <Typography variant="body2" color={alloc.credits_remaining === 0 ? "error" : "text.secondary"}>
                    Remaining: {alloc.credits_remaining}
                  </Typography>
                  <LinearProgress variant="determinate" value={usedPct} color={barCol} sx={{ borderRadius: 2, height: 6, mt: 1 }} />
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                    <FilledButton
                      size="small"
                      sx={(theme) => ({
                        minWidth: 0,
                        px: 1,
                        background: theme.palette.mode === "dark" ? "#0d2137" : "#e3f2fd",
                        color: "primary.safeOnLight",
                        "&:hover": { background: theme.palette.mode === "dark" ? "#1a3a5c" : "#bbdefb", color: "primary.dark" },
                      })}
                      onClick={() => handleEdit(alloc)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </FilledButton>
                    <FilledButton
                      size="small"
                      sx={(theme) => ({
                        minWidth: 0,
                        px: 1,
                        background: theme.palette.mode === "dark" ? "#2a1212" : "#fdecea",
                        color: "error.main",
                        "&:hover": { background: theme.palette.mode === "dark" ? "#3d1a1a" : "#ffcdd2", color: "error.dark" },
                      })}
                      onClick={() => setDeleteTarget(alloc)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </FilledButton>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Allocated</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Used</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Remaining</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Usage</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {!isFetching && allocations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No allocations yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {allocations.map((alloc) => {
                  const usedPct =
                    alloc.credits_allocated > 0
                      ? Math.min((alloc.credits_used / alloc.credits_allocated) * 100, 100)
                      : 0;
                  const barCol =
                    usedPct >= 90 ? "error" : usedPct >= 70 ? "warning" : "primary";

                  return (
                    <TableRow key={alloc.teacher_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {alloc.teacher_name || `Teacher #${alloc.teacher_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{alloc.credits_allocated}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{alloc.credits_used}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={alloc.credits_remaining === 0 ? "error" : "text.primary"}
                        >
                          {alloc.credits_remaining}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usedPct}
                          color={barCol}
                          sx={{ borderRadius: 2, height: 6 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                          <FilledButton
                            size="small"
                            sx={(theme) => ({
                              minWidth: 0,
                              px: 1,
                              background: theme.palette.mode === "dark" ? "#0d2137" : "#e3f2fd",
                              color: "primary.safeOnLight",
                              "&:hover": { background: theme.palette.mode === "dark" ? "#1a3a5c" : "#bbdefb", color: "primary.dark" },
                            })}
                            onClick={() => handleEdit(alloc)}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </FilledButton>
                          <FilledButton
                            size="small"
                            sx={(theme) => ({
                              minWidth: 0,
                              px: 1,
                              background: theme.palette.mode === "dark" ? "#2a1212" : "#fdecea",
                              color: "error.main",
                              "&:hover": { background: theme.palette.mode === "dark" ? "#3d1a1a" : "#ffcdd2", color: "error.dark" },
                            })}
                            onClick={() => setDeleteTarget(alloc)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </FilledButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add / Edit dialog */}
      <AllocationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        existing={editing}
        onSave={refetch}
      />

      {/* Delete confirm dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Allocation</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Remove AI allocation for{" "}
            <strong>{deleteTarget?.teacher_name}</strong>? If allocation mode is still active
            (other allocations exist), this teacher will lose access to the school pool.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <FilledButton
            onClick={() => setDeleteTarget(null)}
            sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
          >
            Cancel
          </FilledButton>
          <StyledLoadingButton
            loading={deleting}
            sx={{ background: "#ef5350", "&:hover": { color: "#ef5350" } }}
            onClick={handleDelete}
          >
            Remove
          </StyledLoadingButton>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
}

// ─── Parent-Pays Settings ───────────────────────────────────────────────────

const NAIRA_PER_CREDIT = 10;

function ParentPaysSettings() {
  const { data, isFetching, refetch: refetchSub } = useGetSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: shareData, isFetching: shareFetching, refetch: refetchShare } = useGetReportCreditShareQuery({ page: 1, per_page: 20 });
  const [switchBillingMode, { isLoading: switching }] = useSwitchBillingModeMutation();
  const [revertBillingMode, { isLoading: reverting }] = useRevertBillingModeMutation();
  const [updateParentPaysConfig, { isLoading: savingMarkup }] = useUpdateParentPaysConfigMutation();
  const [redeemPayg, { isLoading: redeemingPayg }] = useRedeemReportCreditsAsPaygMutation();
  const [redeemCash, { isLoading: redeemingCash }] = useRedeemReportCreditsAsCashMutation();

  const sub = data?.subscription;
  const billingMode = sub?.billing_mode || "school_pays";
  const cancellationPending = sub?.cancellation_pending;
  const pendingCancellationDate = sub?.pending_cancellation_date;
  const currentMarkup = sub?.report_credit_markup ?? 0;
  const currentCovers = sub?.school_covers_report_cost ?? false;

  const [markup, setMarkup] = useState(currentMarkup);
  const [markupDirty, setMarkupDirty] = useState(false);
  const [coversReportCost, setCoversReportCost] = useState(currentCovers);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);

  // Keep local state in sync when subscription data reloads
  React.useEffect(() => {
    if (sub) {
      setMarkup(sub.report_credit_markup ?? 0);
      setCoversReportCost(sub.school_covers_report_cost ?? false);
    }
  }, [sub]);

  const parentPays = billingMode === "parent_pays";
  const baseCost = 50;
  const parentTotal = baseCost + markup;

  const pendingCredits = shareData?.pending_total ?? 0;
  const pendingNaira = pendingCredits * NAIRA_PER_CREDIT;

  const handleToggleCoversReportCost = async (value) => {
    setCoversReportCost(value);
    try {
      await updateParentPaysConfig({ school_covers_report_cost: value }).unwrap();
      toast.success(value ? "School will now cover report costs." : "School cost coverage disabled.");
      refetchSub();
    } catch (err) {
      setCoversReportCost(!value); // revert on failure
      toast.error(err?.data?.error || "Failed to update setting.");
    }
  };

  const handleSwitchToParentPays = async () => {
    try {
      const res = await switchBillingMode({ mode: "parent_pays" }).unwrap();
      if (res.warning) {
        toast.warning(res.warning);
      } else {
        toast.success("Switched to Pay-As-You-Go mode.");
      }
      // Auto-enable school covers report costs when switching to PAYG
      setCoversReportCost(true);
      try {
        await updateParentPaysConfig({ school_covers_report_cost: true }).unwrap();
      } catch (_) {
        // non-critical — user can toggle manually
      }
      refetchSub();
    } catch (err) {
      toast.error(err?.data?.error || "Failed to switch billing mode.");
    }
  };

  const handleRevert = async () => {
    try {
      await revertBillingMode().unwrap();
      toast.success("Billing mode reverted.");
      refetchSub();
    } catch (err) {
      toast.error(err?.data?.error || "Failed to revert.");
    }
  };

  const handleSaveMarkup = async () => {
    try {
      await updateParentPaysConfig({ report_credit_markup: markup }).unwrap();
      toast.success("Markup saved.");
      setMarkupDirty(false);
      refetchSub();
    } catch (err) {
      toast.error(err?.data?.error || "Failed to save markup.");
    }
  };

  const handleRedeemPayg = async () => {
    try {
      await redeemPayg().unwrap();
      toast.success("Credits added to your PAYG balance.");
      refetchShare();
      refetchSub();
    } catch (err) {
      toast.error(err?.data?.error || "Redemption failed.");
    }
  };

  const handleRedeemCash = async () => {
    try {
      await redeemCash().unwrap();
      toast.success("Cash redemption request sent.");
      refetchShare();
    } catch (err) {
      if (err?.data?.error === "bank_details_required") {
        toast.error("Please add your bank account details in Settings before requesting cash.");
      } else {
        toast.error(err?.data?.error || "Redemption failed.");
      }
    }
  };

  if (isFetching) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;

  return (
    <StyledPaper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Billing Method</Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 220, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Subscription</Typography>
          <Typography variant="body2" color="text.secondary">
            Your school pays a subscription fee and report generation is included. Parents can view
            all reports for free. Best for schools that want a simple all-inclusive plan.
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 220, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Pay-As-You-Go</Typography>
          <Typography variant="body2" color="text.secondary">
            Your school pays no subscription fee. Reports are generated freely, and you choose who
            covers the cost — your school can pay per report or have parents spend credits to unlock
            their child's report. You set the markup and earn revenue per unlock. Best for schools
            that want to reduce overhead costs.
          </Typography>
        </Box>
      </Box>

      {/* Mode toggle */}
      {!parentPays ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Current mode: <strong>Subscription</strong>
          </Typography>
          <Button variant="outlined" color="primary" onClick={() => setConfirmSwitchOpen(true)} disabled={switching}>
            Switch to Pay-As-You-Go
          </Button>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Chip label="Active: Pay-As-You-Go" color="success" sx={{ mb: 1 }} />
          {cancellationPending && pendingCancellationDate ? (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: "warning.light", borderRadius: 1 }}>
              <Typography variant="body2">
                Your subscription will be cancelled on{" "}
                <strong>{formatDateOrdinal(pendingCancellationDate)}</strong>.
                You can revert within the grace period.
              </Typography>
              <Button size="small" variant="contained" sx={{ mt: 1, bgcolor: 'background.paper', color: 'warning.dark', '&:hover': { bgcolor: 'grey.100' } }} onClick={() => setConfirmRevertOpen(true)} disabled={reverting}>
                Revert to Subscription
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setConfirmRevertOpen(true)} disabled={reverting}>
                Switch back to Subscription
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Markup config — only shown in parent-pays mode when parents pay to unlock */}
      {parentPays && !coversReportCost && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Report Unlock Pricing</Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap", mb: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Base cost (platform)</Typography>
              <Typography variant="body1"><strong>{baseCost} credits</strong> (₦{baseCost * NAIRA_PER_CREDIT})</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Your markup (credits)</Typography>
              <TextField
                type="number"
                size="small"
                value={markup}
                onChange={(e) => { setMarkup(Number(e.target.value)); setMarkupDirty(true); }}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 100 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Parent pays</Typography>
              <Typography variant="body1"><strong>{parentTotal} credits</strong> (₦{parentTotal * NAIRA_PER_CREDIT})</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">You earn per unlock</Typography>
              <Typography variant="body1" color="success.main"><strong>{markup} credits</strong> (₦{markup * NAIRA_PER_CREDIT})</Typography>
            </Box>
          </Box>
          {markupDirty && (
            <Button variant="contained" size="small" onClick={handleSaveMarkup} disabled={savingMarkup} sx={{ mb: 2 }}>
              Save Markup
            </Button>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>School Covers Report Costs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            When enabled, your school's PAYG wallet is charged <strong>{baseCost} credits</strong> per
            report at generation time. Parents can view reports without unlocking them. If your PAYG
            balance is insufficient, report generation will be blocked until you top up.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Switch
              checked={coversReportCost}
              onChange={(e) => handleToggleCoversReportCost(e.target.checked)}
              disabled={savingMarkup}
            />
            <Typography variant="body2">
              {coversReportCost ? "Enabled — school pays" : "Disabled — parents pay to unlock"}
            </Typography>
          </Box>
          {coversReportCost && (
            <Box sx={{ p: 1.5, bgcolor: "info.50", border: "1px solid", borderColor: "info.200", borderRadius: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Cost per report: <strong>{baseCost} credits</strong> (₦{baseCost * NAIRA_PER_CREDIT}) deducted from your PAYG wallet.
                Markup earnings still apply when parents unlock reports on their own.
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Your Report Credit Earnings</Typography>
          {shareFetching ? (
            <CircularProgress size={20} />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Pending balance: <strong>{pendingCredits} credits</strong> (₦{pendingNaira.toLocaleString()})
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRedeemPayg}
                  disabled={redeemingPayg || pendingCredits === 0}
                >
                  Redeem as PAYG Credits
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  onClick={handleRedeemCash}
                  disabled={redeemingCash || pendingCredits === 0}
                >
                  Redeem as Cash
                </Button>
              </Box>
              {shareData?.items?.length > 0 && (
                <Box sx={{ mt: 2, overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Credits Earned</StyledTableCell>
                        <StyledTableCell>Status</StyledTableCell>
                        <StyledTableCell>Date</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shareData.items.map((row) => (
                        <TableRow key={row.id}>
                          <StyledTableCell>{row.credits_earned}</StyledTableCell>
                          <StyledTableCell>{row.status}</StyledTableCell>
                          <StyledTableCell>{row.created_at ? formatDateOrdinal(row.created_at) : "—"}</StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {/* ── Confirm: Switch to Parent-Pays ── */}
      <Dialog open={confirmSwitchOpen} onClose={() => setConfirmSwitchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch to Pay-As-You-Go?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switching to <strong>Pay-As-You-Go</strong> mode will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2">Cancel your active subscription in 7 days (you can revert within this grace period).</Typography>
            <Typography component="li" variant="body2">Automatically enable <strong>School Covers Report Costs</strong> so parents can view reports for free.</Typography>
            <Typography component="li" variant="body2">Allow you to earn revenue per report unlock based on your markup.</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Any earnings from report unlocks will remain available to redeem after switching.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSwitchOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={switching}
            onClick={() => { setConfirmSwitchOpen(false); handleSwitchToParentPays(); }}
          >
            {switching ? "Switching…" : "Confirm Switch"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm: Revert to School-Pays ── */}
      <Dialog open={confirmRevertOpen} onClose={() => setConfirmRevertOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Revert to Subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Reverting to <strong>Subscription</strong> mode will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2">Switch your school back to Subscription mode.</Typography>
            <Typography component="li" variant="body2">Remove the parent credit requirement for viewing reports.</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Any pending report credit earnings will remain available to redeem.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRevertOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={reverting}
            onClick={() => { setConfirmRevertOpen(false); handleRevert(); }}
          >
            {reverting ? "Reverting…" : "Confirm Revert"}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

function SubscriptionPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [topupOpen, setTopupOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDetailsOpen, setPlanDetailsOpen] = useState(false);
  const [detailsPlan, setDetailsPlan] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const {
    data,
    isLoading,
    refetch,
  } = useGetSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true });

  const { data: plansData, isLoading: plansLoading } = useGetPlansQuery();

  const [cancelSubscription, { isLoading: cancelling }] = useCancelSubscriptionMutation();

  const sub = data?.subscription;
  const usage = data?.usage || {};
  const planConfig = data?.plan_config || {};
  const paygBalance = data?.payg_balance ?? 0;

  const isParentPays = (sub?.billing_mode || "school_pays") === "parent_pays";

  const currentPlanKey = sub?.plan || "free";
  const rawStatus = sub?.status || "inactive";
  const isExpired = sub?.period_end ? new Date(sub.period_end) < new Date() : false;
  const subscriptionIsActive = ["active", "trial"].includes(rawStatus) && !isExpired;
  const displayStatus = isExpired
    ? rawStatus === "trial"
      ? "trial expired"
      : "expired"
    : rawStatus;
  const trialUsed = sub?.trial_used;

  const quota = planConfig?.quota || {};
  const aiQuota = planConfig?.ai_quota || {};

  const handleSelectPlanCard = (plan) => {
    setSelectedPlan(plan);
    setPlanModalOpen(true);
  };

  const handleViewPlanDetails = (plan) => {
    setDetailsPlan(plan);
    setPlanDetailsOpen(true);
  };

  const handleCancelConfirm = async () => {
    try {
      const result = await cancelSubscription({}).unwrap();
      toast.success(result.message);
      setCancelConfirmOpen(false);
      refetch();
    } catch (err) {
      toast.error(err?.message || "Could not cancel subscription.");
    }
  };

  return (
    <div>
      {/* Tab buttons */}
      <Box sx={{ display: "flex", columnGap: 0.5, rowGap: 0.5, flexWrap: "wrap", mb: 1 }}>
        <FilledButton
          sx={{ textTransform: "none" }}
          className={activeTab === "overview" ? "activeTab" : ""}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </FilledButton>
        <FilledButton
          sx={{ textTransform: "none" }}
          className={activeTab === "plans" ? "activeTab" : ""}
          onClick={() => setActiveTab("plans")}
          style={isParentPays ? { display: "none" } : {}}
        >
          Plans &amp; Pricing
        </FilledButton>
        <FilledButton
          sx={{ textTransform: "none" }}
          className={activeTab === "activity" ? "activeTab" : ""}
          onClick={() => setActiveTab("activity")}
        >
          Activity Log
        </FilledButton>
        <FilledButton
          sx={{ textTransform: "none" }}
          className={activeTab === "ai-allocations" ? "activeTab" : ""}
          onClick={() => setActiveTab("ai-allocations")}
        >
          AI Allocations
        </FilledButton>
        <FilledButton
          sx={{ textTransform: "none", display: "none" }}
          className={activeTab === "parent-revenue-share" ? "activeTab" : ""}
          onClick={() => setActiveTab("parent-revenue-share")}
        >
          Parent Revenue Share
        </FilledButton>
        <FilledButton
          sx={{ textTransform: "none" }}
          className={activeTab === "parent-pays" ? "activeTab" : ""}
          onClick={() => setActiveTab("parent-pays")}
        >
          Billing Method
        </FilledButton>
      </Box>

      {/* ── Overview tab ── */}
      {activeTab === "overview" && (
        <>
          {/* Expiration Alert Banner */}
          {isExpired && !isParentPays && (
            <Alert severity="error" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {rawStatus === "trial" ? "Free Trial Expired" : "Subscription Expired"}
              </Typography>
              Your <strong>{planConfig.display_name || currentPlanKey.toUpperCase()}</strong>{" "}
              {rawStatus === "trial" ? "free trial" : "subscription"} expired on{" "}
              {formatDateOrdinal(sub.period_end)}. Please renew your plan or upgrade to restore full included quota and features.
            </Alert>
          )}

          {/* Parent-Pays mode banner */}
          {isParentPays && (
            <StyledPaper sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Chip label="Pay-As-You-Go Mode Active" color="success" />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  Your school is in Pay-As-You-Go mode. Parents can either spend credits to view reports or you can choose to cover report costs yourself from your PAYG balance.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setActiveTab("parent-pays")}
                  sx={{ textTransform: "none" }}
                >
                  Manage Billing Method
                </Button>
              </Box>
            </StyledPaper>
          )}

          {/* Current Plan Card — hidden in Parent-Pays mode */}
          {!isParentPays && (
          <StyledPaper sx={{ padding: "0 0 24px 0" }}>
            <Typography variant="formHeading" sx={{ padding: "20px" }}>
              Current Plan
            </Typography>
            {isLoading && <LinearProgress />}
            <Divider sx={{ margin: "0 20px 20px 20px" }} />

            {!isLoading && (
              <Box sx={{ px: 2.5 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: PLAN_COLORS[currentPlanKey] }}>
                        {planConfig.display_name || currentPlanKey.charAt(0).toUpperCase() + currentPlanKey.slice(1)}
                      </Typography>
                      <Chip
                        label={displayStatus.toUpperCase()}
                        color={statusColor(displayStatus)}
                        size="small"
                      />
                    </Box>

                    {sub?.period_end && (
                      <Typography
                        variant="body2"
                        color={isExpired ? "error.main" : "text.secondary"}
                        sx={{ fontWeight: isExpired ? 600 : 400 }}
                      >
                        {isExpired
                          ? `Expired on: ${formatDateOrdinal(sub.period_end)}`
                          : rawStatus === "cancelled"
                          ? `Access until: ${formatDateOrdinal(sub.period_end)}`
                          : `Renews: ${formatDateOrdinal(sub.period_end)}`}
                      </Typography>
                    )}
                    {sub?.trial_end && rawStatus === "trial" && !isExpired && (
                      <Typography variant="body2" color="info.main" sx={{ mt: 0.5 }}>
                        Trial ends: {formatDateOrdinal(sub.trial_end)}
                      </Typography>
                    )}
                    {sub?.pending_plan && (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        Scheduled downgrade to{" "}
                        <strong>{sub.pending_plan.charAt(0).toUpperCase() + sub.pending_plan.slice(1)}</strong>{" "}
                        at next renewal.
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {isExpired && currentPlanKey !== "free" ? (
                        <>
                          <FilledButton
                            startIcon={<UpgradeIcon />}
                            onClick={() => {
                              const currentPlanObj = plansData?.plans?.find((p) => p.key === currentPlanKey);
                              if (currentPlanObj) {
                                handleSelectPlanCard(currentPlanObj);
                              } else {
                                setActiveTab("plans");
                              }
                            }}
                          >
                            Renew {planConfig.display_name || currentPlanKey.toUpperCase()}
                          </FilledButton>
                          <FilledButton
                            sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
                            onClick={() => setActiveTab("plans")}
                          >
                            Change Plan
                          </FilledButton>
                        </>
                      ) : (
                        <>
                          {!trialUsed && currentPlanKey === "free" && (
                            <FilledButton
                              startIcon={<UpgradeIcon />}
                              onClick={() => setTrialOpen(true)}
                            >
                              Start Free Trial
                            </FilledButton>
                          )}
                          <FilledButton
                            startIcon={<UpgradeIcon />}
                            onClick={() => setActiveTab("plans")}
                          >
                            {currentPlanKey === "free" ? "Upgrade" : "Change Plan"}
                          </FilledButton>
                          {subscriptionIsActive && currentPlanKey !== "free" && (
                            <FilledButton
                              sx={{ background: "#ef5350", "&:hover": { color: "#ef5350" } }}
                              onClick={() => setCancelConfirmOpen(true)}
                            >
                              Cancel
                            </FilledButton>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </StyledPaper>
          )}

          {/* Usage Quotas — hidden in Parent-Pays mode */}
          {!isParentPays && (
          <StyledPaper sx={{ padding: "0 0 24px 0" }}>
            <Typography variant="formHeading" sx={{ padding: "20px" }}>
              Usage This Period
            </Typography>
            {isLoading && <LinearProgress />}
            <Divider sx={{ margin: "0 20px 20px 20px" }} />
            <Box sx={{ px: 2.5 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <QuotaBar
                    label="Reports Generated"
                    used={usage.reports_used ?? 0}
                    limit={(quota.reports !== null && quota.reports !== undefined && quota.reports !== -1 && quota.reports !== 0)
                      ? quota.reports + (usage.reports_quota_adjustment ?? 0)
                      : quota.reports}
                  />
                  <QuotaBar
                    label="Quizzes Created"
                    used={usage.quizzes_used ?? 0}
                    limit={(quota.quizzes !== null && quota.quizzes !== undefined && quota.quizzes !== -1 && quota.quizzes !== 0)
                      ? quota.quizzes + (usage.quizzes_quota_adjustment ?? 0)
                      : quota.quizzes}
                  />
                  <QuotaBar
                    label="Invoices Created"
                    used={usage.invoices_used ?? 0}
                    limit={(quota.invoices !== null && quota.invoices !== undefined && quota.invoices !== -1 && quota.invoices !== 0)
                      ? quota.invoices + (usage.invoices_quota_adjustment ?? 0)
                      : quota.invoices}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <QuotaBar
                    label="AI Comment Actions (School)"
                    used={usage.ai_actions_used ?? 0}
                    limit={aiQuota.comment_actions}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {sub?.period_end
                    ? `All quotas reset on ${formatDateOrdinal(sub.period_end)}.`
                    : 'All quotas reset at the start of each subscription period.'}
                </Typography>
                <FilledButton
                  size="small"
                  startIcon={<SwapHorizIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => setTransferOpen(true)}
                >
                  Redistribute Quota
                </FilledButton>
              </Box>
            </Box>
          </StyledPaper>
          )}

          {/* Quota Transfer Dialog */}
          <QuotaTransferDialog
            open={transferOpen}
            onClose={() => setTransferOpen(false)}
            quota={quota}
            usage={usage}
            onSuccess={refetch}
          />

          {/* PAYG Wallet */}
          <StyledPaper sx={{ padding: "0 0 24px 0" }}>
            <Typography variant="formHeading" sx={{ padding: "20px" }}>
              Pay-As-You-Go Credits
            </Typography>
            <Divider sx={{ margin: "0 20px 20px 20px" }} />
            <Box sx={{ px: 2.5 }}>
              {planConfig.payg_available ? (
                <>
                  <Box
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      background: theme.palette.mode === "dark" ? "#1a2a3a" : "#f0f8ff",
                      borderRadius: 2,
                      mb: 2,
                    })}
                  >
                    <AccountBalanceWalletOutlinedIcon sx={{ color: "primary.safeOnLight", fontSize: 32 }} />
                    <Box>
                      <Typography variant="formSubHeading">PAYG BALANCE</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {paygBalance.toLocaleString()} credits
                      </Typography>
                    </Box>
                    <Box sx={{ ml: "auto" }}>
                      <FilledButton
                        startIcon={<CreditCardOutlinedIcon />}
                        onClick={() => setTopupOpen(true)}
                      >
                        Top Up
                      </FilledButton>
                    </Box>
                  </Box>
                  <Box
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.mode === "dark" ? "#2a3a4a" : "#c8e0f4"}`,
                      borderRadius: 2,
                      p: 2,
                      background: theme.palette.mode === "dark" ? "#111e2a" : "#f7fbff",
                    })}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      How PAYG Credits Work
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      PAYG credits are automatically used when your plan quota for the current
                      term runs out — so your school never gets blocked mid-term.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                      Credit Rate
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      <strong>₦10 = 1 credit</strong>
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                      Cost Per Action
                    </Typography>
                    <Box
                      component="table"
                      sx={{ width: "100%", borderCollapse: "collapse", "& td": { py: 0.5, pr: 2 } }}
                    >
                      <tbody>
                        {[
                          { action: "Generate a report", cost: "50 credits" },
                          { action: "Create a quiz", cost: "50 credits" },
                          { action: "Create an invoice", cost: "50 credits" },
                          { action: "AI comment (per student)", cost: "1 credit" },
                        ].map(({ action, cost }) => (
                          <tr key={action}>
                            <td>
                              <Typography variant="body2">{action}</Typography>
                            </td>
                            <td>
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                {cost}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                </>
              ) : (
                <Alert
                  severity="info"
                  action={
                    <FilledButton
                      size="small"
                      startIcon={<UpgradeIcon />}
                      onClick={() => setActiveTab("plans")}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      View Plans
                    </FilledButton>
                  }
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    PAYG not available on Free plan
                  </Typography>
                  <Typography variant="body2">
                    Upgrade to Basic or higher to top up school credits for extra reports,
                    quizzes, and invoices beyond your plan quota.
                  </Typography>
                </Alert>
              )}
            </Box>
          </StyledPaper>
        </>
      )}

      {/* ── Plans tab ── */}
      {activeTab === "plans" && !isParentPays && (
        <StyledPaper sx={{ padding: "0 0 24px 0" }}>
          <Typography variant="formHeading" sx={{ padding: "20px" }}>
            Plans &amp; Pricing
          </Typography>
          {plansLoading && <LinearProgress />}
          <Divider sx={{ margin: "0 20px 20px 20px" }} />
          <Box sx={{ px: 2.5 }}>
            <Grid container spacing={2}>
              {plansData?.plans?.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.key}>
                  <PlanCard
                    plan={plan}
                    currentPlanKey={currentPlanKey}
                    subscriptionIsActive={subscriptionIsActive}
                    onSelect={handleSelectPlanCard}
                    onViewDetails={handleViewPlanDetails}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </StyledPaper>
      )}

      {/* ── Activity tab ── */}
      {activeTab === "activity" && <ActivityLog />}

      {/* ── AI Allocations tab ── */}
      {activeTab === "ai-allocations" && <AIAllocations />}

      {/* ── Parent Revenue Share tab ── */}
      {activeTab === "parent-revenue-share" && <ParentRevenueShare />}

      {/* ── Parent-Pays Settings tab ── */}
      {activeTab === "parent-pays" && <ParentPaysSettings />}

      {/* ── Modals ── */}
      <TopupCreditsModal
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        onSuccess={() => { refetch(); setTopupOpen(false); }}
      />

      <TrialModal
        open={trialOpen}
        onClose={() => setTrialOpen(false)}
        onSuccess={() => { refetch(); }}
      />

      <PlanPaymentModal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        selectedPlan={selectedPlan}
        currentPlanKey={currentPlanKey}
        subscriptionIsActive={subscriptionIsActive}
        onSuccess={() => { refetch(); }}
      />

      <PlanDetailsModal
        open={planDetailsOpen}
        onClose={() => setPlanDetailsOpen(false)}
        plan={detailsPlan}
      />

      {/* Cancel Confirm Dialog */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Your subscription will be cancelled. You will retain access until the end of the current
            billing period, then be moved to the Free plan.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <FilledButton
            onClick={() => setCancelConfirmOpen(false)}
            sx={{ background: "#9e9e9e", "&:hover": { color: "#9e9e9e" } }}
          >
            Keep Subscription
          </FilledButton>
          <StyledLoadingButton
            loading={cancelling}
            sx={{ background: "#ef5350", "&:hover": { color: "#ef5350" } }}
            onClick={handleCancelConfirm}
          >
            Yes, Cancel
          </StyledLoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SubscriptionPage;
